import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './clientPendingInvoicesCard.css';

const money = (n) =>
  `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Home-page card surfacing the client's PENDING INVOICES total with a quick way
 * to settle them (2026-07-16). Renders nothing when there is nothing due.
 * The actual multi-rail "Pay pending" checkout (wallet / card ≤5k / bank) lives on
 * the Invoices page — this is the prominent home entry point into it.
 */
export default function ClientPendingInvoicesCard({ total: totalProp, wallets: walletsProp }) {
  const navigate = useNavigate();
  // When the parent seeds `total` (home page), treat it as already loaded and skip
  // the self-fetch so the card paints in-frame. Undefined → standalone self-fetch.
  const seeded = totalProp !== undefined;
  const [total, setTotal] = useState(seeded ? Number(totalProp || 0) : 0);
  const [loaded, setLoaded] = useState(seeded);
  // Separate entity wallets: [{ wallet_client_id, label, pending_total }] —
  // each pool is settled from its own wallet, so each gets its own line.
  const [wallets, setWallets] = useState(Array.isArray(walletsProp) ? walletsProp : []);

  useEffect(() => {
    if (seeded) return; // parent supplied the total — no round-trip, no pop-in.
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get('/api/client-requests/invoices/pending-total');
        if (!cancelled) {
          setTotal(Number(res.data?.pending_total || 0));
          setWallets(Array.isArray(res.data?.wallets) ? res.data.wallets : []);
        }
      } catch {
        if (!cancelled) setTotal(0);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [seeded]);

  const entityDue = (Array.isArray(walletsProp) ? walletsProp : wallets).filter((w) => Number(w?.pending_total || 0) > 0.009);
  if (!loaded || (total <= 0.009 && entityDue.length === 0)) return null;

  return (
    <>
      {total > 0.009 && (
        <div className="cpiCard">
          <div className="cpiCard__main">
            <div className="cpiCard__label">Pending invoices</div>
            <div className="cpiCard__amount">{money(total)}</div>
            <div className="cpiCard__sub">Settle your outstanding invoices to keep your account up to date.</div>
          </div>
          <button type="button" className="cpiCard__btn" onClick={() => navigate('/client/invoices')}>
            View &amp; pay
          </button>
        </div>
      )}
      {entityDue.map((w) => (
        <div className="cpiCard" key={w.wallet_client_id}>
          <div className="cpiCard__main">
            <div className="cpiCard__label">Pending invoices — {w.label}</div>
            <div className="cpiCard__amount">{money(Number(w.pending_total || 0))}</div>
            <div className="cpiCard__sub">Paid from the {w.label} wallet.</div>
          </div>
          <button type="button" className="cpiCard__btn" onClick={() => navigate(`/client/invoices/pay?wallet=${w.wallet_client_id}`)}>
            View &amp; pay
          </button>
        </div>
      ))}
    </>
  );
}
